import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAuthBlob } from '@utils/blobUrl';

export interface ModelBlobState {
  /** The downloaded bytes, or null while in flight / after a failure. */
  blob: Blob | null;
  /** True once the download has failed and is not being retried. */
  failed: boolean;
  /** Re-runs the download. Calls made while one is in flight collapse into it. */
  retry: () => void;
}

const IDLE: ModelBlobState = { blob: null, failed: false, retry: () => {} };

const HTML_TYPE = /^text\/html\b/i;

const fetchDirect = async (url: string): Promise<Blob> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
};

export interface FetchArtifactOptions {
  /** The bytes are meant to be a web page, so an HTML answer is the file, not a miss. */
  readonly acceptHtml?: boolean;
  /** Asked for by an app's page: a refusal must not sign the user out. */
  readonly skipAuthRedirect?: boolean;
}

/**
 * Download an artifact's bytes: API URLs ({@code authed}) through the authed
 * client, presigned and file-server URLs directly.
 */
export const fetchArtifactBlob = async (
  url: string,
  authed: boolean,
  { acceptHtml = false, skipAuthRedirect = false }: FetchArtifactOptions = {},
): Promise<Blob> => {
  const blob = authed ? await fetchAuthBlob(url, { skipAuthRedirect }) : await fetchDirect(url);
  // A host that does not serve the path answers with the SPA's index.html and a 200.
  if (!acceptHtml && HTML_TYPE.test(blob.type)) {
    throw new Error('Expected file bytes, got an HTML page');
  }
  return blob;
};

/**
 * Download model bytes and hand back the {@link Blob} itself.
 *
 * <p>Deliberately does <em>not</em> return an object URL. The viewer used to
 * receive a `blob:` URL and read it back with a second `fetch`, which failed
 * with `TypeError: Failed to fetch` whenever the URL was revoked while that
 * read was still in flight — routine when a card scrolls out of the viewport
 * mid-load (AIB-7393). Passing the Blob removes the lifetime coupling.</p>
 *
 * <p>{@code authed} picks the transport: API URLs go through the authed client,
 * presigned and file-server URLs are fetched directly. It is part of the cache
 * key because the two transports can resolve the same URL differently.</p>
 *
 * <p>Backed by React Query, so the cache key handles what would otherwise be
 * manual bookkeeping: state resets when the URL changes, concurrent mounts of
 * the same model share one request, and bytes already downloaded are reused
 * when a card scrolls back into view. `retry` is `refetch`, which is also how
 * repeated clicks stay collapsed into the in-flight request.</p>
 */
export function useModelBlob(
  url: string | undefined,
  enabled: boolean,
  authed: boolean,
  /** Identifies the bytes when `url` changes without them, as a re-signed URL does. */
  cacheKey?: string,
  options: FetchArtifactOptions = {},
): ModelBlobState {
  const active = Boolean(enabled && url);
  const acceptHtml = options.acceptHtml ?? false;
  const { data, isError, isFetching, refetch } = useQuery<Blob>({
    queryKey: ['modelBlob', cacheKey ?? url, authed, acceptHtml],
    queryFn: async (): Promise<Blob> => {
      // The `active` gate guarantees a URL by the time this runs; throwing on
      // the unreachable branch narrows the type and guards a gating regression.
      if (!url) throw new Error('useModelBlob: url is required');
      const blob = await fetchArtifactBlob(url, authed, { acceptHtml });
      if (blob.size === 0) throw new Error('Expected model bytes, got an empty file');
      return blob;
    },
    enabled: active,
    // Both presigned and artifact download URLs are immutable for their
    // lifetime, so the bytes for a given URL never change.
    staleTime: Infinity,
    // The caller's failure UX is a placeholder with an explicit retry, which
    // beats a retry storm against a flaky S3.
    retry: false,
  });

  // `cancelRefetch: false` is what makes repeated clicks collapse into the
  // request already running instead of cancelling and re-issuing it.
  const retry = useCallback(() => {
    void refetch({ cancelRefetch: false });
  }, [refetch]);

  if (!active) return IDLE;
  // Suppress the failure while a retry is in flight so the caller shows its
  // loading state again rather than leaving the error on screen.
  return { blob: data ?? null, failed: isError && !isFetching, retry };
}
